import { useEffect, useState } from "react";
import { Box, Button, Grid2 } from "@mui/material";
import { LockReset } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { Link, useNavigate, useParams } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, FieldError, useForm } from "react-hook-form";
import dayjs from "dayjs";

import { InstitutionFormStyled } from "./style";
import ValidationSchema from "./validation";

import ResetPasswordDialog from "@/pages/UserManagement/Institution/partials/ResetPasswordDialog";
import { useCheckPermissionAccess } from "@/hooks/useCheckPermissionAccess";
import { OperationAccess, PagesAccess } from "@/constants/enums/permissions";
import { useCreateInstitution, useInstitutionById, useUpdateInstitution } from "@/api/hooks/institution";
import DashboardHeader from "@/components/app/DashboardHeader";
import ROUTE_CONSTANTS from "@/constants/route-constants";
import FormText from "@/components/kit/FormGenerator/FormText";
import FormTextarea from "@/components/kit/FormGenerator/FormTextarea";
import FormSelectImage from "@/components/kit/FormGenerator/FormSelectImage";
import { IInstitutionCreateBody, IInstitutionUpdateBody } from "@/api/types/institution";

export interface IInstitutionData {
    name: string;
    email: string;
    password?: string;
    maxConcurrentDevices: number;
    startDate: string;
    endDate: string;
    notes?: string;
    mediaId?: number;
}

const defaultValues: IInstitutionData = {
    name: "",
    email: "",
    password: "",
    maxConcurrentDevices: 1,
    startDate: "",
    endDate: "",
    notes: "",
    mediaId: undefined,
};

export default function InstitutionForm() {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEditing = Boolean(id);
    const [resetPasswordOpen, setResetPasswordOpen] = useState(false);

    const updateAccess = useCheckPermissionAccess(PagesAccess.INSTITUTION, OperationAccess.UPDATE);

    const { data: InstitutionData, isSuccess } = useInstitutionById(Number(id));
    const { mutate: createInstitution, isPending } = useCreateInstitution();
    const { mutate: updateInstitution, isPending: updatePending } = useUpdateInstitution();

    const {
        control,
        formState: { errors },
        handleSubmit,
        reset,
    } = useForm<IInstitutionData>({
        context: { isEditing },
        defaultValues,
        mode: "all",
        resolver: yupResolver(ValidationSchema),
    });

    useEffect(() => {
        if (InstitutionData && InstitutionData.data && isSuccess) {
            const { name, email, maxConcurrentDevices, startDate, endDate, notes } = InstitutionData.data;

            reset({
                name,
                email,
                maxConcurrentDevices,
                startDate: dayjs(startDate).format("YYYY-MM-DD"),
                endDate: dayjs(endDate).format("YYYY-MM-DD"),
                notes: notes || "",
            });
        }
    }, [isSuccess, InstitutionData, reset]);

    const onSubmit = (data: IInstitutionData) => {
        if (id) {
            const body: IInstitutionUpdateBody = {
                name: data.name,
                email: data.email,
                maxConcurrentDevices: Number(data.maxConcurrentDevices),
                startDate: data.startDate,
                endDate: data.endDate,
            };

            if (data.notes) body.notes = data.notes;
            if (data.mediaId) body.mediaId = data.mediaId;

            updateInstitution(
                { id: Number(id), body },
                {
                    onSuccess: () => navigate(ROUTE_CONSTANTS.USER_MANAGEMENT.INSTITUTIONS.ROOT.ABSOLUTE),
                }
            );
        } else {
            const body: IInstitutionCreateBody = {
                name: data.name,
                email: data.email,
                password: data.password || "",
                maxConcurrentDevices: Number(data.maxConcurrentDevices),
                startDate: data.startDate,
                endDate: data.endDate,
            };

            if (data.notes) body.notes = data.notes;
            if (data.mediaId) body.mediaId = data.mediaId;

            createInstitution(body, {
                onSuccess: () => navigate(ROUTE_CONSTANTS.USER_MANAGEMENT.INSTITUTIONS.ROOT.ABSOLUTE),
            });
        }
    };

    return (
        <InstitutionFormStyled>
            <DashboardHeader
                title={isEditing ? "Update Institution" : "Create Institution"}
                titleSuffix={
                    <Box sx={{ display: "flex", alignItems: "center", gap: "0.5em" }}>
                        {isEditing && updateAccess && (
                            <Button
                                color="warning"
                                startIcon={<LockReset />}
                                variant="outlined"
                                onClick={() => setResetPasswordOpen(true)}
                            >
                                Reset Password
                            </Button>
                        )}
                        <Button component={Link} to={ROUTE_CONSTANTS.USER_MANAGEMENT.INSTITUTIONS.ROOT.ABSOLUTE}>
                            Back to Institutions List
                        </Button>
                    </Box>
                }
            />
            <Box className="add-box">
                <form
                    style={{ maxWidth: "850px", margin: "0 auto", padding: "0 0 2em 0" }}
                    onSubmit={handleSubmit(onSubmit)}
                >
                    <Grid2 container spacing={2}>
                        <Grid2 size={{ xs: 12, md: 6 }}>
                            <Controller
                                key="name"
                                control={control}
                                name="name"
                                render={({ field }) => (
                                    <FormText
                                        className="name field"
                                        errorMessage={errors["name"] as FieldError}
                                        field={{ ...field }}
                                        label="Name"
                                        placeholder="Enter institution name"
                                        props={{ required: true }}
                                    />
                                )}
                            />
                        </Grid2>
                        <Grid2 size={{ xs: 12, md: 6 }}>
                            <Controller
                                key="email"
                                control={control}
                                name="email"
                                render={({ field }) => (
                                    <FormText
                                        className="email field"
                                        errorMessage={errors["email"] as FieldError}
                                        field={{ ...field }}
                                        label="Email"
                                        placeholder="Enter institution email"
                                        props={{ required: true }}
                                    />
                                )}
                            />
                        </Grid2>
                        {!isEditing && (
                            <Grid2 size={{ xs: 12, md: 6 }}>
                                <Controller
                                    key="password"
                                    control={control}
                                    name="password"
                                    render={({ field }) => (
                                        <FormText
                                            className="password field"
                                            errorMessage={errors["password"] as FieldError}
                                            field={{ ...field }}
                                            label="Password"
                                            placeholder="Enter password"
                                            props={{ required: true, type: "password" }}
                                        />
                                    )}
                                />
                            </Grid2>
                        )}
                        <Grid2 size={{ xs: 12, md: 6 }}>
                            <Controller
                                key="maxConcurrentDevices"
                                control={control}
                                name="maxConcurrentDevices"
                                render={({ field }) => (
                                    <FormText
                                        className="maxConcurrentDevices field"
                                        errorMessage={errors["maxConcurrentDevices"] as FieldError}
                                        field={{ ...field }}
                                        label="Max Concurrent Devices"
                                        placeholder="Enter max concurrent devices"
                                        props={{ required: true, type: "number" }}
                                    />
                                )}
                            />
                        </Grid2>
                        <Grid2 size={{ xs: 12, md: 6 }}>
                            <Controller
                                key="startDate"
                                control={control}
                                name="startDate"
                                render={({ field }) => (
                                    <FormText
                                        className="startDate field"
                                        errorMessage={errors["startDate"] as FieldError}
                                        field={{ ...field }}
                                        label="Start Date"
                                        placeholder="Select start date"
                                        props={{ required: true, type: "date", InputLabelProps: { shrink: true } }}
                                    />
                                )}
                            />
                        </Grid2>
                        <Grid2 size={{ xs: 12, md: 6 }}>
                            <Controller
                                key="endDate"
                                control={control}
                                name="endDate"
                                render={({ field }) => (
                                    <FormText
                                        className="endDate field"
                                        errorMessage={errors["endDate"] as FieldError}
                                        field={{ ...field }}
                                        label="End Date"
                                        placeholder="Select end date"
                                        props={{ required: true, type: "date", InputLabelProps: { shrink: true } }}
                                    />
                                )}
                            />
                        </Grid2>
                        <Grid2 size={{ xs: 12 }}>
                            <Controller
                                key="notes"
                                control={control}
                                name="notes"
                                render={({ field }) => (
                                    <FormTextarea
                                        className="notes field"
                                        errorMessage={errors["notes"] as FieldError}
                                        field={{ ...field }}
                                        label="Notes"
                                        placeholder="Enter notes"
                                        props={{}}
                                    />
                                )}
                            />
                        </Grid2>
                        <Grid2 size={{ xs: 12, md: 6 }}>
                            <Controller
                                key="mediaId"
                                control={control}
                                name="mediaId"
                                render={({ field }) => (
                                    <FormSelectImage
                                        className="mediaId field"
                                        errorMessage={errors["mediaId"] as FieldError}
                                        field={{ ...field }}
                                        label="Logo"
                                        placeholder="(Max Size: 3Mb)"
                                        props={{}}
                                    />
                                )}
                            />
                        </Grid2>
                        <Grid2 alignItems="center" justifyContent="center" size={{ xs: 12 }}>
                            <br />
                            <LoadingButton
                                fullWidth
                                className="success"
                                disabled={isPending || updatePending}
                                loading={isPending || updatePending}
                                type="submit"
                                variant="contained"
                            >
                                {isEditing ? "Update" : "Create"}
                            </LoadingButton>
                        </Grid2>
                    </Grid2>
                </form>
            </Box>
            <ResetPasswordDialog
                institutionId={id ? Number(id) : null}
                institutionName={InstitutionData?.data.name}
                open={resetPasswordOpen}
                onClose={() => setResetPasswordOpen(false)}
            />
        </InstitutionFormStyled>
    );
}
